import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function MedicalSocialServices() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/medical-social-services#webpage`,
    url: `${siteUrl}/services/medical-social-services`,
    name: 'Medical Social Services Las Vegas | Celebrity Home Health',
    description: 'Home-based medical social services in Las Vegas — counseling, community resource coordination, financial assistance guidance, and family support. Medicare certified.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'SocialWork',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Medical Social Services', item: `${siteUrl}/services/medical-social-services` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Medical Social Services Las Vegas | Celebrity Home Health';
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-medical-social" />
      <Header />
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/6d9c4fd36e40207417bb3adffe5445af.jpg" alt="Medical Social Services" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6"><i className="ri-heart-pulse-line"></i> Medical Social Services</div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Medical Social Services<br /><span className="text-amber-400">at Home</span> in Las Vegas</h1>
            <p className="text-slate-300 text-xl leading-relaxed mb-8">Our licensed medical social workers provide emotional, social, and practical support — helping patients and families navigate illness, recovery, and access community resources. Medicare certified.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Support</a>
              <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i> 866-939-0330</a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">How We Help</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Social Services We Provide</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Holistic support addressing the emotional, social, and practical challenges of illness and recovery.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-mental-health-line', title: 'Emotional Support', desc: 'Counseling and support to help patients and families cope with illness, grief, and life changes.' },
              { icon: 'ri-community-line', title: 'Community Resources', desc: 'Connecting patients to local programs, support groups, transportation, and community services.' },
              { icon: 'ri-money-dollar-circle-line', title: 'Financial Guidance', desc: 'Assistance navigating insurance, Medicare/Medicaid benefits, and financial assistance programs.' },
              { icon: 'ri-team-line', title: 'Family Counseling', desc: 'Education and counseling to help families understand diagnoses and care needs.' },
              { icon: 'ri-file-list-3-line', title: 'Advance Care Planning', desc: 'Guidance on advance directives, living wills, and healthcare power of attorney.' },
              { icon: 'ri-alarm-warning-line', title: 'Crisis Intervention', desc: 'Immediate support and resources during emotional or situational crises.' },
            ].map((item, i) => (
              <div key={i} className="bg-stone-50 p-7 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4"><i className={`${item.icon} text-amber-700 text-lg`}></i></div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">You Don&apos;t Have to Face This Alone</h2>
          <p className="text-slate-400 text-lg mb-8">Our medical social workers are here to support you and your family every step of the way. Contact us today.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Care Now</a>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">View All Services</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
